import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { NavLink, useLocation } from "react-router-dom";
import CogIcon from "./icons/CogIcon";
import ShieldCheckIcon from "./icons/ShieldCheckIcon";
import { UserPlus } from "lucide-react";
import LogoutIcon from "./icons/LogoutIcon";
import { League, UserRole } from "../types";
import AccountingIcon from "./icons/AccountingIcon";
import CreditCardIcon from "./icons/CreditCardIcon";
import DocumentTextIcon from "./icons/DocumentTextIcon";
import ShieldExclamationIcon from "./icons/ShieldExclamationIcon";
import TableCellsIcon from "./icons/TableCellsIcon";
import CalendarDaysIcon from "./icons/CalendarDaysIcon";
import LocationPinIcon from "./icons/LocationPinIcon";
import ClipboardCheckIcon from "./icons/ClipboardCheckIcon";
import UsersGroupIcon from "./icons/UsersGroupIcon";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { logAndThrow } from "../utils/logging";
import { useNotificationContext } from "../contexts/NotificationContext";
import { makeNotifier } from "../utils/notify";
import { useJobCenter } from "../hooks/useJobCenter";
import { forceResetScrollLock } from "../hooks/useScrollLock";
import { useIncomingTeamsBadge } from "../hooks/useIncomingTeamsBadge";
import { useAuditReviewBadge } from "../hooks/useAuditReviewBadge";
import JobsIcon from "./icons/JobsIcon";
import JobCenterPanel from "./JobCenterPanel";
import NotificationCenter from "./NotificationCenter";
import { useCanSeeNotifications } from "../hooks/useAdminNotifications";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import { useDesignSystem } from "../lib/design/designSystem";
import LanguageSwitcher from "./ui/LanguageSwitcher";
import { GlassSelect } from "./ui/GlassComponents";
import GlassSelectWithSearch from "./ui/GlassSelectWithSearch";
import HeaderNavGroup from "./ui/HeaderNavGroup";
import FullScreenLoader from "./ui/FullScreenLoader";
import { useTranslation } from "react-i18next";
import { useLeagueModules } from "@/hooks/useLeagueModules";
import type { ModuleKey } from "@/lib/modules/registry";
import { canOpenSettings } from "@/lib/permissions/settingsAccess";
import { getLeagueFullLabel, getLeagueShortLabel } from "@/lib/leagues/leagueSelection";

interface HeaderProps {
  seasons: string[];
  leagues: League[];
}

type NavigationTab =
  | "planning"
  | "missions"
  | "classements"
  | "disciplinary"
  | "officials"
  | "finances"
  | "clubs"
  | "settings"
  | "audit"
  | "accounting"
  | "virements"
  | "etats"
  | "news";

const Header: React.FC<HeaderProps> = ({ seasons, leagues }) => {
  const { user, permissions } = useAuth();
  const location = useLocation();
  const { t: tHeader, i18n } = useTranslation('header');
  const { t: tCommon } = useTranslation('common');
  // Show the league name in the active UI language only (saves header space so the
  // app name isn't truncated). Falls back to the other language if one is missing.
  const isArabicUi = (i18n.language || '').toLowerCase().startsWith('ar');
  // Full name in the options and the trigger tooltip; the compact desktop
  // trigger shows the short form (« LRF Annaba », « LIRF »).
  const leagueLabel = (l: League) => getLeagueFullLabel(l, isArabicUi);
  const leagueShortLabel = (l: League) => getLeagueShortLabel(l, isArabicUi);
  const { showNotification } = useNotificationContext();
  const notify = makeNotifier((m, t) => showNotification(m, t));
  const { activeCount } = useJobCenter();
  // Modules activés pour la ligue sélectionnée : masque les onglets des modules
  // désactivés (les routes correspondantes sont gardées côté AppRouter).
  const { modules } = useLeagueModules();
  const [showJobs, setShowJobs] = useState(false);
  const [focusJobId, setFocusJobId] = useState<string | null>(null);

  // Single close path for the Job Center panel: the X button, the backdrop
  // click (both inside JobCenterPanel, via the onClose prop) and the header's
  // own bell button all route through this so a stale focusJobId can never
  // survive a close and leak into the next open.
  const closeJobCenter = useCallback(() => {
    setShowJobs(false);
    setFocusJobId(null);
  }, []);

  const toggleJobCenter = useCallback(() => {
    // Side effect lives in the handler body, not inside the setShowJobs
    // updater — updater functions are expected to be pure (React 18 Strict
    // Mode invokes them twice in dev).
    if (showJobs) {
      setFocusJobId(null);
    }
    setShowJobs((prev) => !prev);
  }, [showJobs]);

  const currentLeagueId = useLeagueSeasonStore(
    (state) => state.currentLeagueId
  );

  // Nav badges count for the selected league only, like the pages they open.
  const leagueIds = useMemo(
    () => (currentLeagueId ? [currentLeagueId] : []),
    [currentLeagueId]
  );
  // Incoming-teams badge: teams awaiting placement in the selected league.
  const { data: incomingBadgeData } = useIncomingTeamsBadge(leagueIds);
  const incomingCount = incomingBadgeData?.count ?? 0;

  // Audit-review badge: stadium audits of the selected league submitted by
  // officials and awaiting validation. Only fetched for users who can validate.
  const canValidateAudits =
    permissions.can("validate", "stadium_audit") ||
    permissions.can("update", "stadium_audit");
  const { data: auditReviewBadgeData } = useAuditReviewBadge(
    leagueIds,
    canValidateAudits
  );
  const auditReviewCount = auditReviewBadgeData?.count ?? 0;

  // Gate for the media Notification Center. Same permission as the /media nav
  // item and route guard, so the three can no longer drift apart.
  // Bell: media reviewers, disciplinary users, officials link-notice audience — one rule.
  const canSeeNotifications = useCanSeeNotifications();

  // Allow other components to open the Job Center panel by dispatching a
  // `job-center:open` window event, optionally focused on one job. Used by
  // FafEventsPanel's inline progress widget and by the stadium audit planner's
  // order toasts.
  useEffect(() => {
    const handler = (e: Event) => {
      const jobId = (e as CustomEvent<{ jobId?: string }>).detail?.jobId ?? null;
      setFocusJobId(jobId);
      setShowJobs(true);
    };
    window.addEventListener('job-center:open', handler);
    return () => window.removeEventListener('job-center:open', handler);
  }, []);

  // Safety net: clear any leaked body-scroll lock on navigation, so a modal
  // that failed to release its lock can't leave the next page unscrollable.
  useEffect(() => {
    forceResetScrollLock();
  }, [location.pathname]);
  const {
    layout,
    surfaces,
    typography,
    components: componentTokens,
  } = useDesignSystem();
  const currentSeason = useLeagueSeasonStore((state) => state.currentSeason);
  const setCurrentSeason = useLeagueSeasonStore(
    (state) => state.setCurrentSeason
  );
  const setCurrentLeagueId = useLeagueSeasonStore(
    (state) => state.setCurrentLeagueId
  );

  const headerClass = useMemo(
    () =>
      clsx(
        surfaces.header,
        "sticky top-0 z-30 transition-all duration-500 ease-in-out relative",
        "bg-slate-950/95 backdrop-blur-xl border-b-2 border-white/20 shadow-2xl"
      ),
    [surfaces.header]
  );

  const headerInnerClass = useMemo(
    () => clsx(layout.pageGutter, "py-1 sm:py-1.5 lg:py-2"),
    [layout.pageGutter]
  );

  const titleClass = useMemo(
    () =>
      clsx(
        typography.sectionTitle,
        "text-sm sm:text-base lg:text-xl xl:text-2xl font-bold ms-1 sm:ms-2 lg:ms-3 truncate"
      ),
    [typography.sectionTitle]
  );

  const navBarClass = useMemo(
    () => clsx(componentTokens.tabBar, "items-center gap-1"),
    [componentTokens.tabBar]
  );

  const navButtonBaseClass = useMemo(
    () =>
      clsx(
        componentTokens.tabButton.base,
        "px-2 py-1.5 text-xs rounded-lg min-h-[32px]"
      ),
    [componentTokens.tabButton.base]
  );

  const navButtonActiveClass = useMemo(
    () => componentTokens.tabButton.active,
    [componentTokens.tabButton.active]
  );

  const navButtonInactiveClass = useMemo(
    () => componentTokens.tabButton.inactive,
    [componentTokens.tabButton.inactive]
  );

  const getNavLinkClass = useCallback(
    (path: string) =>
      clsx(
        navButtonBaseClass,
        location.pathname === path
          ? navButtonActiveClass
          : navButtonInactiveClass
      ),
    [
      location.pathname,
      navButtonActiveClass,
      navButtonInactiveClass,
      navButtonBaseClass,
    ]
  );

  const iconButtonBaseClass = useMemo(
    () =>
      "inline-flex items-center justify-center rounded-full p-1.5 transition-colors duration-200",
    []
  );

  const iconButtonActiveClass = useMemo(
    () => clsx(componentTokens.tabButton.active, "rounded-full border"),
    [componentTokens.tabButton.active]
  );

  const iconButtonInactiveClass = useMemo(
    () => clsx(componentTokens.tabButton.inactive, "rounded-full"),
    [componentTokens.tabButton.inactive]
  );

  const getIconNavLinkClass = useCallback(
    (path: string) =>
      clsx(
        iconButtonBaseClass,
        location.pathname === path
          ? iconButtonActiveClass
          : iconButtonInactiveClass
      ),
    [
      location.pathname,
      iconButtonActiveClass,
      iconButtonBaseClass,
      iconButtonInactiveClass,
    ]
  );

  const jobsButtonClass = useMemo(
    () =>
      clsx(
        iconButtonBaseClass,
        showJobs ? iconButtonActiveClass : iconButtonInactiveClass,
        "relative"
      ),
    [
      iconButtonActiveClass,
      iconButtonBaseClass,
      iconButtonInactiveClass,
      showJobs,
    ]
  );

  const jobsBadgeClass = useMemo(
    () =>
      "absolute -top-1 -end-1 bg-primary-500 text-white text-[9px] font-semibold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1",
    []
  );

  const mobileMenuButtonBaseClass = useMemo(
    () => clsx(componentTokens.tabButton.inactive, "rounded-md p-1.5"),
    [componentTokens.tabButton.inactive]
  );

  const mobileMenuButtonActiveClass = useMemo(
    () => clsx(componentTokens.tabButton.active, "rounded-md p-1.5"),
    [componentTokens.tabButton.active]
  );

  const mobileNavLinkClass = useCallback(
    (isActive: boolean) =>
      clsx(
        navButtonBaseClass,
        "w-full justify-start text-sm",
        isActive ? navButtonActiveClass : navButtonInactiveClass
      ),
    [navButtonActiveClass, navButtonBaseClass, navButtonInactiveClass]
  );

  const mobileMenuContainerClass = useMemo(
    () =>
      clsx(
        "xl:hidden",
        surfaces.panelStrong,
        "border-t border-white/10",
        "backdrop-blur-xl bg-slate-900/95",
        "animate-slideDown origin-top"
      ),
    [surfaces.panelStrong]
  );

  // Morphing header scroll behavior
  type ScrollMode = "full" | "compact" | "hidden";
  const [scrollMode, setScrollMode] = useState<ScrollMode>("full");
  const [isHovering, setIsHovering] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const scrollThreshold = 10; // Minimum scroll distance to trigger changes

  // Scroll detection with morphing behavior
  useEffect(() => {
    const updateHeaderMode = () => {
      const currentScrollY = window.scrollY;
      const isDesktop = window.innerWidth >= 1280; // xl breakpoint

      // If hovering, always show full mode
      if (isHovering) {
        setScrollMode("full");
        lastScrollY.current = currentScrollY;
        ticking.current = false;
        return;
      }

      // At the top of the page - full mode
      if (currentScrollY < scrollThreshold) {
        setScrollMode("full");
        lastScrollY.current = currentScrollY;
        ticking.current = false;
        return;
      }

      // Desktop: slide the entire bar up on scroll-down to free vertical space,
      // and bring it back on scroll-up (the header transform handles "hidden").
      if (isDesktop) {
        // Scrolling down past threshold - hide the whole bar
        if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
          setScrollMode("hidden");
        }
        // Scrolling up - reveal full bar
        else if (currentScrollY < lastScrollY.current) {
          setScrollMode("full");
        }
      }
      // Mobile: morph to compact on scroll-down, but never hide — the header
      // (and its Job Center button) must stay reachable everywhere.
      else {
        if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
          setScrollMode("compact");
        } else if (currentScrollY < lastScrollY.current) {
          setScrollMode("full");
        }
      }

      lastScrollY.current = currentScrollY;
      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateHeaderMode);
        ticking.current = true;
      }
    };

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isHovering]);

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      console.log("[Header] Starting logout process...");
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("[Header] Logout error from Supabase:", {
          message: error.message,
          status: error.status,
          name: error.name,
          stack: error.stack,
        });
        setIsLoggingOut(false);
        notify.error(
          tHeader("user.logout_error", { message: error.message })
        );
        return logAndThrow("auth.signOut", error);
      }
      console.log("[Header] Logout successful");
      // Force page reload to ensure clean state and show login page
      window.location.reload();
    } catch (e) {
      // Routing will handle session loss anyway; just log
      setIsLoggingOut(false);
      console.error("[Header] Unexpected error during logout:", e);
      notify.error((e as any)?.message || String(e));
    }
  };

  const allNavItems: {
    path: string;
    to?: string;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
    badge?: number;
    badgeTitle?: string;
    moduleKey: ModuleKey;
    featureKey?: string;
  }[] = [
    {
      path: "/planning",
      moduleKey: "planning",
      label: tHeader('nav.planning'),
      icon: null,
      visible:
        permissions.can("view", "assignments") ||
        permissions.can("view", "matches"),
    },
    {
      path: "/calendar",
      moduleKey: "calendar_generation",
      to: incomingCount > 0 ? "/calendar#incoming" : "/calendar",
      label: tHeader('nav.calendar'),
      icon: <CalendarDaysIcon className="h-4 w-4 me-2" />,
      visible: permissions.can("view", "calendar"),
      badge: incomingCount > 0 ? incomingCount : undefined,
      badgeTitle: incomingCount > 0 ? tHeader('incoming_badge_tooltip', { count: incomingCount }) : undefined,
    },
    {
      path: "/missions",
      moduleKey: "stadiums_audits",
      featureKey: "audit_planning",
      label: tHeader('nav.missions'),
      icon: <ClipboardCheckIcon className="h-4 w-4 me-2" />,
      visible:
        permissions.can("view", "stadium_audit") ||
        permissions.can("assign", "missions") ||
        permissions.can("view", "missions"),
      badge: auditReviewCount > 0 ? auditReviewCount : undefined,
      badgeTitle:
        auditReviewCount > 0
          ? tHeader('audit_review_badge_tooltip', { count: auditReviewCount })
          : undefined,
    },
    {
      path: "/classements",
      moduleKey: "rankings",
      label: tHeader('nav.classements'),
      icon: <TableCellsIcon className="h-4 w-4 me-2" />,
      visible: permissions.can("view", "rankings"),
    },
    {
      path: "/disciplinary",
      moduleKey: "disciplinary",
      label: tHeader('nav.disciplinary'),
      icon: <ShieldExclamationIcon className="h-4 w-4 me-2" />,
      visible: permissions.can("view", "disciplinary"),
    },
    {
      path: "/management",
      moduleKey: "club_registry",
      label: tHeader('nav.management'),
      icon: <UsersGroupIcon className="h-4 w-4 me-2" />,
      visible:
        permissions.can("view", "officials") ||
        permissions.can("view", "disciplinary") ||
        permissions.can("manage", "officials"),
    },
    /*{
      path: "/officials",
      label: tHeader('nav.officials'),
      icon: null,
      visible: permissions.can("view", "officials"),
    },*/
    {
      path: "/officials-management",
      moduleKey: "officials",
      label: tHeader('nav.officials'),
      icon: <ShieldCheckIcon className="h-4 w-4 me-2" />,
      visible:
        permissions.can("manage", "officials") ||
        permissions.can("bulk_action", "officials") ||
        permissions.can("view", "officials"),
    },
    {
      // Responsable Arbitres : propose les arbitres, la ligue valide (spec 2026-10-30).
      path: "/referee-proposals",
      moduleKey: "planning",
      label: tHeader('nav.referee_proposals'),
      icon: <UserPlus className="h-4 w-4 me-2" />,
      visible: permissions.can("create", "assignment_proposal"),
    },
    {
      path: "/clubs",
      moduleKey: "club_registry",
      featureKey: "clubs",
      label: tHeader('nav.clubs'),
      icon: null,
      visible: permissions.can("view", "clubs"),
    },
    {
      path: "/stadiums",
      moduleKey: "stadiums_audits",
      featureKey: "stadiums",
      label: tHeader('nav.stadiums'),
      icon: <LocationPinIcon className="h-4 w-4 me-2" />,
      visible: permissions.can("view", "stadium"),
    },
    {
      path: "/accounting",
      moduleKey: "accounting",
      label: tHeader('nav.accounting'),
      icon: <AccountingIcon className="h-4 w-4 me-2" />,
      visible: permissions.can("view", "accounting"),
    },
    {
      path: "/finances",
      moduleKey: "payments_treasury",
      featureKey: "finances_dashboard",
      label: tHeader('nav.finances'),
      icon: null,
      visible: permissions.can("view", "finances"),
    },
    {
      path: "/virements",
      moduleKey: "payments_treasury",
      featureKey: "virements",
      label: tHeader('nav.virements'),
      icon: <CreditCardIcon className="h-4 w-4 me-2" />,
      visible: permissions.can("view", "finances"),
    },
    {
      path: "/etats",
      moduleKey: "payments_treasury",
      featureKey: "etats",
      label: tHeader('nav.etats'),
      icon: <DocumentTextIcon className="h-4 w-4 me-2" />,
      visible: permissions.can("view", "finances"),
    },
    // News management
    {
      path: "/news",
      moduleKey: "news",
      label: tHeader('nav.news'),
      icon: null,
      visible: permissions.can("view", "news"),
    },
    // Media Accreditation section - single entry point
    {
      path: "/media",
      moduleKey: "media_accreditation",
      label: tHeader('nav.media'),
      icon: null,
      visible: permissions.can("view", "media"),
    },
  ];

  // Filter navigation based on role
  const navItems = useMemo(() => {
    const moduleVisible = (item: (typeof allNavItems)[number]) =>
      item.featureKey
        ? modules.isFeatureEnabled(item.moduleKey, item.featureKey)
        : modules.isModuleEnabled(item.moduleKey);

    // REFREE_MANAGER should only see the Officials management tab and his proposals page
    const isRefreeManager = user?.role === UserRole.REFREE_MANAGER ||
                            user?.primaryRole === UserRole.REFREE_MANAGER ||
                            user?.roles?.some((r) => r.role === UserRole.REFREE_MANAGER);

    if (isRefreeManager) {
      return allNavItems.filter(
        (item) =>
          ["/officials-management", "/referee-proposals"].includes(item.path) && item.visible && moduleVisible(item),
      );
    }

    return allNavItems.filter((item) => item.visible && moduleVisible(item));
  }, [allNavItems, modules, user?.role, user?.primaryRole, user?.roles]);

  // Group navigation items for better organization
  const navGroups = useMemo(() => {
    const groups = [
      {
        id: "planning",
        items: navItems.filter((item) =>
          ["/planning", "/calendar"].includes(item.path)
        ),
      },
      {
        id: "officials",
        items: navItems.filter((item) =>
          ["/officials", "/officials-management", "/referee-proposals"].includes(item.path)
        ),
      },
      {
        id: "competition",
        items: navItems.filter((item) =>
          ["/classements", "/clubs", "/stadiums", "/missions"].includes(item.path)
        ),
      },
      {
        id: "discipline",
        items: navItems.filter((item) => item.path === "/disciplinary"),
      },
      {
        id: "management",
        items: navItems.filter((item) => item.path === "/management"),
      },
      {
        id: "finance",
        items: navItems.filter((item) =>
          ["/accounting", "/finances", "/virements", "/etats"].includes(
            item.path
          )
        ),
      },
      {
        id: "news",
        items: navItems.filter((item) =>
          ["/news"].includes(item.path)
        ),
      },
      {
        id: "media",
        items: navItems.filter((item) =>
          ["/media"].includes(item.path)
        ),
      },
    ];
    return groups.filter((group) => group.items.length > 0);
  }, [navItems]);

  // Color configuration for each navigation group
  const groupColorConfig: Record<string, { active: string; inactive: string; divider: string }> = {
    planning: {
      active: "bg-gradient-to-r from-primary-500/35 to-primary-400/25 border-primary-400/60 text-primary-100 shadow-[0_0_12px_-3px_rgba(59,130,246,0.5)]",
      inactive: "bg-primary-500/15 border-primary-400/30 text-primary-200/90 hover:bg-primary-500/25 hover:border-primary-400/50 hover:text-primary-100",
      divider: "bg-primary-400/30",
    },
    officials: {
      active: "bg-gradient-to-r from-emerald-500/35 to-emerald-400/25 border-emerald-400/60 text-emerald-100 shadow-[0_0_12px_-3px_rgba(16,185,129,0.5)]",
      inactive: "bg-emerald-500/15 border-emerald-400/30 text-emerald-200/90 hover:bg-emerald-500/25 hover:border-emerald-400/50 hover:text-emerald-100",
      divider: "bg-emerald-400/30",
    },
    competition: {
      active: "bg-gradient-to-r from-cyan-500/35 to-cyan-400/25 border-cyan-400/60 text-cyan-100 shadow-[0_0_12px_-3px_rgba(6,182,212,0.5)]",
      inactive: "bg-cyan-500/15 border-cyan-400/30 text-cyan-200/90 hover:bg-cyan-500/25 hover:border-cyan-400/50 hover:text-cyan-100",
      divider: "bg-cyan-400/30",
    },
    discipline: {
      active: "bg-gradient-to-r from-rose-500/35 to-rose-400/25 border-rose-400/60 text-rose-100 shadow-[0_0_12px_-3px_rgba(244,63,94,0.5)]",
      inactive: "bg-rose-500/15 border-rose-400/30 text-rose-200/90 hover:bg-rose-500/25 hover:border-rose-400/50 hover:text-rose-100",
      divider: "bg-rose-400/30",
    },
    management: {
      active: "bg-gradient-to-r from-violet-500/35 to-violet-400/25 border-violet-400/60 text-violet-100 shadow-[0_0_12px_-3px_rgba(139,92,246,0.5)]",
      inactive: "bg-violet-500/15 border-violet-400/30 text-violet-200/90 hover:bg-violet-500/25 hover:border-violet-400/50 hover:text-violet-100",
      divider: "bg-violet-400/30",
    },
    finance: {
      active: "bg-gradient-to-r from-amber-500/35 to-amber-400/25 border-amber-400/60 text-amber-100 shadow-[0_0_12px_-3px_rgba(245,158,11,0.5)]",
      inactive: "bg-amber-500/15 border-amber-400/30 text-amber-200/90 hover:bg-amber-500/25 hover:border-amber-400/50 hover:text-amber-100",
      divider: "bg-amber-400/30",
    },
    news: {
      active: "bg-gradient-to-r from-sky-500/35 to-sky-400/25 border-sky-400/60 text-sky-100 shadow-[0_0_12px_-3px_rgba(14,165,233,0.5)]",
      inactive: "bg-sky-500/15 border-sky-400/30 text-sky-200/90 hover:bg-sky-500/25 hover:border-sky-400/50 hover:text-sky-100",
      divider: "bg-sky-400/30",
    },
    media: {
      active: "bg-gradient-to-r from-teal-500/35 to-teal-400/25 border-teal-400/60 text-teal-100 shadow-[0_0_12px_-3px_rgba(20,184,166,0.5)]",
      inactive: "bg-teal-500/15 border-teal-400/30 text-teal-200/90 hover:bg-teal-500/25 hover:border-teal-400/50 hover:text-teal-100",
      divider: "bg-teal-400/30",
    },
  };

  // Get nav link class with group-specific colors
  const getNavLinkClassForGroup = useCallback(
    (path: string, groupId: string) => {
      const colors = groupColorConfig[groupId] || groupColorConfig.planning;
      const isActive = location.pathname === path;
      
      return clsx(
        navButtonBaseClass,
        isActive
          ? colors.active
          : clsx("border-transparent text-white/60", colors.inactive)
      );
    },
    [location.pathname, navButtonBaseClass]
  );

  // Dynamic classes based on scroll mode
  const headerDynamicClass = useMemo(() => {
    if (scrollMode === "compact") {
      return clsx(headerClass, "backdrop-blur-2xl bg-slate-950/98");
    }
    return headerClass;
  }, [headerClass, scrollMode]);

  const innerDynamicClass = useMemo(() => {
    if (scrollMode === "compact") {
      return clsx(layout.pageGutter, "py-1 transition-all duration-500");
    }
    return headerInnerClass;
  }, [headerInnerClass, layout.pageGutter, scrollMode]);

  return (
    <>
      {/* Fullscreen loader during logout */}
      {isLoggingOut && createPortal(
        <FullScreenLoader />,
        document.body
      )}
      <header
        className={headerDynamicClass}
        style={{
          transform:
            scrollMode === "hidden" ? "translateY(-100%)" : "translateY(0)",
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
      <div className={innerDynamicClass}>
        <div className="flex items-center justify-between h-10 sm:h-12 lg:h-14">
          {/* Logo and Title - Responsive */}
          {/* The brand keeps « Moubarati » whole when a nav group expands
              (min width at xl, Arabic subtitle only from 2xl). */}
          <div className="flex items-center min-w-0 xl:min-w-[13rem] me-2">
            <img
              src="/tool.png"
              alt="App Logo"
              className="h-8 w-10 sm:h-9 sm:w-11 lg:h-10 lg:w-12 flex-shrink-0 transition-all duration-300"
            />
            <h1
              className={clsx(titleClass, "hidden xl:inline")}
              title="Moubarati - مباراتي"
            >
              Moubarati<span className="hidden 2xl:inline"> - مباراتي</span>
            </h1>
          </div>

          {/* Desktop Navigation (hidden on mobile/tablet) */}
          <div className="hidden xl:flex items-center gap-2 flex-1 justify-end">
            {scrollMode === "full" && (
              <nav
                className={clsx(navBarClass, "transition-opacity duration-500")}
              >
                {navGroups.map((group, groupIdx) => {
                  const colors = groupColorConfig[group.id] || groupColorConfig.planning;
                  return (
                    <React.Fragment key={group.id}>
                      {groupIdx > 0 && (
                        <div className={clsx("h-6 w-px mx-0.5", colors.divider)} />
                      )}
                      <HeaderNavGroup
                        groupId={group.id}
                        items={group.items}
                        colorConfig={colors}
                        currentPath={location.pathname}
                        navButtonBaseClass={navButtonBaseClass}
                        showIcons={true}
                      />
                    </React.Fragment>
                  );
                })}
                <div className="h-6 w-px bg-white/10 mx-1" />
                {(user?.role === UserRole.SUPER_ADMIN ||
                  user?.primaryRole === UserRole.SUPER_ADMIN ||
                  user?.roles?.some(
                    (r) => r.role === UserRole.SUPER_ADMIN
                  )) && (
                  <NavLink
                    to="/audit"
                    className={getIconNavLinkClass("/audit")}
                    aria-label={tHeader('aria.audit_log')}
                    title={tHeader('aria.audit_log')}
                  >
                    <ShieldCheckIcon className="h-4 w-4" />
                  </NavLink>
                )}
                {canOpenSettings(permissions) && (
                  <NavLink
                    to="/settings"
                    className={getIconNavLinkClass("/settings")}
                    aria-label={tHeader('aria.settings')}
                    title={tHeader('aria.settings')}
                  >
                    <CogIcon className="h-4 w-4" />
                  </NavLink>
                )}
                {/* Hide job center for REFREE_MANAGER */}
                {user?.role !== UserRole.REFREE_MANAGER &&
                  user?.primaryRole !== UserRole.REFREE_MANAGER &&
                  !user?.roles?.some((r) => r.role === UserRole.REFREE_MANAGER) && (
                  <button
                    className={jobsButtonClass}
                    onClick={toggleJobCenter}
                    title={tHeader('aria.tasks')}
                    aria-label={tHeader('aria.tasks')}
                  >
                    <JobsIcon size={16} />
                    {activeCount > 0 && (
                      <span className={jobsBadgeClass}>{activeCount}</span>
                    )}
                  </button>
                )}
              </nav>
            )}
            <div
              className={clsx(
                scrollMode === "compact"
                  ? "ms-0"
                  : "ms-0 border-s border-white/10 ps-0",
                "flex items-center flex-shrink-0 transition-all duration-500"
              )}
            >
              <div>
                <label htmlFor="season-select" className="sr-only">
                  {tHeader('selectors.season')}
                </label>
                <GlassSelect
                  id="season-select"
                  value={currentSeason || ""}
                  onChange={(e) => setCurrentSeason(e.target.value)}
                  aria-label={tHeader('selectors.select_season')}
                  disabled={seasons.length === 0}
                  containerClassName="space-y-0"
                  size="xs"
                  className="font-medium w-auto min-w-[9.5rem]"
                >
                  {seasons.length === 0 && (
                    <option value="">{tHeader('selectors.no_season')}</option>
                  )}
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </GlassSelect>
              </div>
              <div>
                <label htmlFor="league-select" className="sr-only">
                  {tHeader('selectors.league')}
                </label>
                <GlassSelectWithSearch
                  id="league-select"
                  value={currentLeagueId || ""}
                  onChange={(e) => setCurrentLeagueId(e.target.value)}
                  aria-label={tHeader('selectors.select_league')}
                  searchPlaceholder={tHeader('selectors.search_league')}
                  disabled={leagues.length === 0}
                  containerClassName="space-y-0"
                  size="xs"
                  menuMinWidth={440}
                  wrapOptionLabels
                  className="font-medium w-auto max-w-[220px]"
                >
                  {leagues.length === 0 && (
                    <option value="">{tHeader('selectors.no_league')}</option>
                  )}
                  {leagues.map((league) => (
                    <option
                      key={league.id}
                      value={league.id}
                      data-trigger-label={leagueShortLabel(league)}
                    >
                      {leagueLabel(league)}
                    </option>
                  ))}
                </GlassSelectWithSearch>
              </div>
              {canSeeNotifications && <NotificationCenter />}
              {/* Hide language switch for REFREE_MANAGER */}
              {user?.role !== UserRole.REFREE_MANAGER &&
                user?.primaryRole !== UserRole.REFREE_MANAGER &&
                !user?.roles?.some((r) => r.role === UserRole.REFREE_MANAGER) && (
                <LanguageSwitcher className="ms-2" />
              )}
              <button
                onClick={handleLogout}
                className={clsx(iconButtonBaseClass, iconButtonInactiveClass)}
                title={tHeader('user.logout')}
              >
                <LogoutIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mobile/Tablet Compact Controls (xl and below) */}
          <div className="flex xl:hidden items-center gap-1 sm:gap-2">
            <button
              className={jobsButtonClass}
              onClick={toggleJobCenter}
              title={tHeader('aria.tasks')}
              aria-label={tHeader('aria.tasks')}
            >
              <JobsIcon size={16} />
              {activeCount > 0 && (
                <span className={jobsBadgeClass}>{activeCount}</span>
              )}
            </button>

            {canSeeNotifications && <NotificationCenter />}

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={clsx(
                "transition-colors",
                isMobileMenuOpen
                  ? mobileMenuButtonActiveClass
                  : mobileMenuButtonBaseClass
              )}
              aria-label={tHeader('aria.menu')}
              title={tHeader('aria.menu')}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Dropdown Menu */}
      {isMobileMenuOpen && (
        <div className={mobileMenuContainerClass}>
          <div className="px-3 py-3 space-y-3 max-h-[calc(100vh-3rem)] overflow-y-auto scrollbar-hidden">
            {/* Grouped Navigation Links */}
            {navGroups.map((group, groupIdx) => {
              const colors = groupColorConfig[group.id] || groupColorConfig.planning;
              return (
                <div key={group.id} className="space-y-1">
                  {group.items.length > 0 && (
                    <>
                      {groupIdx > 0 && <div className={clsx("h-px my-2", colors.divider)} />}
                      {group.items.map((item, itemIdx) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={({ isActive }) =>
                            clsx(
                              "relative",
                              navButtonBaseClass,
                              "w-full justify-start text-sm",
                              isActive
                                ? colors.active
                                : clsx("border-transparent text-white/60", colors.inactive)
                            )
                          }
                          style={{
                            animationDelay: `${groupIdx * 50 + itemIdx * 30}ms`,
                          }}
                        >
                          <div className="flex items-center">
                            {item.icon && (
                              <span className="me-3">{item.icon}</span>
                            )}
                            <span>{item.label}</span>
                          </div>
                          {(item.badge ?? 0) > 0 && (
                            <span
                              className="absolute -top-1 -end-1 bg-amber-500 text-white text-[9px] font-semibold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1"
                              title={item.badgeTitle}
                            >
                              {item.badge}
                            </span>
                          )}
                        </NavLink>
                      ))}
                    </>
                  )}
                </div>
              );
            })}

            {/* System & Admin Section */}
            <div className="space-y-1 pt-2 border-t border-white/10">
              {canOpenSettings(permissions) && (
                <NavLink
                  to="/settings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) => mobileNavLinkClass(isActive)}
                >
                  <div className="flex items-center">
                    <CogIcon className="h-4 w-4 me-3" />
                    <span>{tHeader('nav.settings')}</span>
                  </div>
                </NavLink>
              )}

              {(user?.role === UserRole.SUPER_ADMIN ||
                user?.primaryRole === UserRole.SUPER_ADMIN ||
                user?.roles?.some((r) => r.role === UserRole.SUPER_ADMIN)) && (
                <NavLink
                  to="/audit"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) => mobileNavLinkClass(isActive)}
                >
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-4 w-4 me-3" />
                    <span>{tHeader('nav.audit')}</span>
                  </div>
                </NavLink>
              )}
            </div>

            {/* Season & League Selectors */}
            <div className="pt-3 space-y-3 border-t border-white/10">
              <div className="px-3">
                <GlassSelect
                  id="mobile-season-select"
                  label={tHeader('selectors.season')}
                  value={currentSeason || ""}
                  onChange={(e) => setCurrentSeason(e.target.value)}
                  disabled={seasons.length === 0}
                  size="sm"
                  className="w-full"
                >
                  {seasons.length === 0 && (
                    <option value="">{tHeader('selectors.no_season')}</option>
                  )}
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </GlassSelect>
              </div>

              <div className="px-3">
                <GlassSelectWithSearch
                  id="mobile-league-select"
                  label={tHeader('selectors.league')}
                  value={currentLeagueId || ""}
                  onChange={(e) => setCurrentLeagueId(e.target.value)}
                  searchPlaceholder={tHeader('selectors.search_league')}
                  disabled={leagues.length === 0}
                  size="sm"
                  className="w-full"
                >
                  {leagues.length === 0 && (
                    <option value="">{tHeader('selectors.no_league')}</option>
                  )}
                  {leagues.map((league) => (
                    <option key={league.id} value={league.id}>
                      {leagueLabel(league)}
                    </option>
                  ))}
                </GlassSelectWithSearch>
              </div>
            </div>

            {/* User Info & Logout */}
            <div className="pt-3 border-t border-white/10">
              <div className="px-3 py-2">
                <p
                  className={clsx(
                    typography.body,
                    "text-sm font-medium text-white"
                  )}
                >
                  {user?.full_name}
                </p>
                <p className="text-xs text-white/60">{user?.role}</p>
              </div>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
                className={mobileNavLinkClass(false)}
              >
                <LogoutIcon className="h-4 w-4 me-3" />
                <span>{tHeader('user.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
    {showJobs && (
      <JobCenterPanel focusJobId={focusJobId} onClose={closeJobCenter} />
    )}
    </>
  );
};

export default Header;
